// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

import { RemoteMarkdown } from '@/components/remote/RemoteMarkdown';
import { MorphIcon } from '@/components/ui/morph-icon';
import { cn } from '@/lib/utils';
import type {
  AgentMessageItem,
  HumanInputItem,
  WorkItem,
} from '@web/lib/remoteWorkItemModel';
import { Bot, CircleHelp, Info, ListChecks, MessageSquare } from 'lucide';
import { FinalAnswerBlock } from './FinalAnswerBlock';
import { ReasoningSummaryBlock } from './ReasoningSummaryBlock';
import { ToolCallDisclosure } from './ToolCallDisclosure';
import { createToolTextPreview, formatToolValue } from './ToolInputOutputPanel';

export interface WorkItemRendererProps {
  item: WorkItem;
  className?: string;
  onAnswerHumanInput?: (item: HumanInputItem) => void;
  toolExpanded?: boolean;
  onToolExpandedChange?: (expanded: boolean) => void;
  onHelpfulAnswer?: (item: AgentMessageItem) => void;
  onUnhelpfulAnswer?: (item: AgentMessageItem) => void;
}

function CommentaryBlock({ item }: { item: AgentMessageItem }) {
  return (
    <section className="min-w-0 py-2" aria-label="Agent commentary">
      <header className="mb-2 flex items-center gap-2">
        <MorphIcon
          icon={MessageSquare}
          aria-hidden
          className="h-4 w-4 shrink-0 text-ds-icon-neutral-muted-default"
        />
        <h3 className="m-0 text-body-sm font-medium text-ds-text-neutral-default-default">
          Agent update
        </h3>
      </header>
      {item.text?.trim() ? (
        <RemoteMarkdown content={item.text} />
      ) : (
        <p className="m-0 text-body-sm text-ds-text-neutral-muted-default">
          No commentary was recorded.
        </p>
      )}
    </section>
  );
}

function StructuredResponse({ value }: { value: unknown }) {
  const preview = createToolTextPreview(formatToolValue(value));
  return (
    <>
      <pre className="m-0 mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-code-surface p-2 font-mono text-label-xs text-ds-text-neutral-default-default">
        {preview.text}
      </pre>
      {preview.truncated && (
        <p className="m-0 mt-1 text-label-xs text-ds-text-neutral-muted-default">
          Response preview was truncated.
        </p>
      )}
    </>
  );
}

function HumanInputBlock({
  item,
  onAnswer,
}: {
  item: HumanInputItem;
  onAnswer?: (item: HumanInputItem) => void;
}) {
  const hasResponse = item.response !== undefined;
  const waiting = item.status === 'waiting';
  const title = hasResponse
    ? 'Question answered'
    : item.status === 'interrupted'
      ? 'Question cancelled'
      : item.status === 'failed'
        ? 'Question could not be completed'
        : item.status === 'completed'
          ? 'Question closed'
          : waiting
            ? 'Agent needs your input'
            : 'Agent question pending';
  const content = (
    <>
      <header className="mb-1.5 flex items-center gap-2">
        <MorphIcon
          icon={CircleHelp}
          aria-hidden
          className="h-4 w-4 shrink-0 text-ds-text-status-pending-strong-default"
        />
        <h3 className="m-0 text-body-sm font-semibold text-ds-text-neutral-default-default">
          {title}
        </h3>
      </header>
      <p className="m-0 whitespace-pre-wrap break-words text-body-sm text-ds-text-neutral-default-default">
        {item.question?.trim() || 'The agent requested additional input.'}
      </p>
      {hasResponse && <StructuredResponse value={item.response} />}
      {!hasResponse && !waiting && (
        <p className="m-0 mt-2 text-label-xs text-ds-text-neutral-muted-default">
          No response was recorded for this question.
        </p>
      )}
    </>
  );
  const className =
    'min-w-0 rounded-xl border border-solid border-ds-border-status-pending-subtle-default bg-ds-bg-status-pending-subtle-default p-3';

  if (waiting && !hasResponse && onAnswer) {
    return (
      <button
        type="button"
        className={cn(
          className,
          'block w-full cursor-pointer text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring-brand-default-focus'
        )}
        aria-label="Agent question"
        onClick={() => onAnswer(item)}
      >
        {content}
      </button>
    );
  }

  return (
    <section className={className} aria-label="Agent question">
      {content}
    </section>
  );
}

function PlanOrNoticeBlock({ item }: { item: WorkItem }) {
  const plan = item.type === 'plan';
  const text = 'text' in item && typeof item.text === 'string' ? item.text : '';
  const Icon = plan ? ListChecks : Info;
  return (
    <section
      className="flex min-w-0 items-start gap-2 py-2"
      aria-label={plan ? 'Agent plan' : 'Agent notice'}
    >
      <MorphIcon
        icon={Icon}
        aria-hidden
        className="mt-0.5 h-4 w-4 shrink-0 text-ds-icon-neutral-muted-default"
      />
      <div className="min-w-0 flex-1">
        <h3 className="m-0 mb-1 text-body-sm font-medium text-ds-text-neutral-default-default">
          {plan ? 'Plan' : 'Update'}
        </h3>
        {text.trim() ? (
          <RemoteMarkdown content={text} />
        ) : (
          <p className="m-0 text-body-sm text-ds-text-neutral-muted-default">
            No details were recorded.
          </p>
        )}
      </div>
    </section>
  );
}

function AgentCallBlock({ item }: { item: WorkItem }) {
  const prompt =
    'prompt' in item && typeof item.prompt === 'string' ? item.prompt : '';
  const resultSummary =
    'resultSummary' in item && typeof item.resultSummary === 'string'
      ? item.resultSummary
      : '';
  return (
    <section
      className="min-w-0 rounded-xl border border-solid border-ds-border-neutral-subtle-default p-3"
      aria-label="Subagent work"
    >
      <header className="mb-1.5 flex items-center gap-2">
        <MorphIcon
          icon={Bot}
          aria-hidden
          className="h-4 w-4 shrink-0 text-ds-icon-neutral-muted-default"
        />
        <h3 className="m-0 text-body-sm font-medium text-ds-text-neutral-default-default">
          Subagent
        </h3>
      </header>
      {prompt && (
        <p className="m-0 whitespace-pre-wrap break-words text-body-sm text-ds-text-neutral-muted-default">
          {prompt}
        </p>
      )}
      {resultSummary && (
        <div className="mt-2">
          <RemoteMarkdown content={resultSummary} />
        </div>
      )}
      {!prompt && !resultSummary && (
        <p className="m-0 text-body-sm text-ds-text-neutral-muted-default">
          Subagent work details are available in its agent block.
        </p>
      )}
    </section>
  );
}

function UnknownWorkItem({ item }: { item: WorkItem }) {
  const runtimeType =
    item.type === 'unknown' && item.originalType
      ? item.originalType
      : typeof (item as { type?: unknown }).type === 'string'
        ? (item as { type: string }).type
        : 'unknown';
  return (
    <section
      className="flex min-w-0 items-start gap-2 rounded-xl border border-dashed border-ds-border-neutral-subtle-default p-3"
      aria-label="Unsupported work item"
    >
      <MorphIcon
        icon={Info}
        aria-hidden
        className="mt-0.5 h-4 w-4 shrink-0 text-ds-icon-neutral-muted-default"
      />
      <p className="m-0 min-w-0 break-words text-body-sm text-ds-text-neutral-muted-default">
        This client cannot display the “{runtimeType}” work item yet.
      </p>
    </section>
  );
}

/**
 * Renders one normalized work item. Turn containers should select the root
 * final answer separately and place it outside their work disclosure.
 */
export function WorkItemRenderer({
  item,
  className,
  onAnswerHumanInput,
  toolExpanded,
  onToolExpandedChange,
  onHelpfulAnswer,
  onUnhelpfulAnswer,
}: WorkItemRendererProps) {
  let content;
  switch (item.type) {
    case 'reasoning_summary':
      content = <ReasoningSummaryBlock item={item} />;
      break;
    case 'tool_call':
      content = (
        <ToolCallDisclosure
          item={item}
          expanded={toolExpanded}
          onExpandedChange={onToolExpandedChange}
        />
      );
      break;
    case 'agent_message':
      content =
        item.phase === 'final_answer' ? (
          <FinalAnswerBlock
            item={item}
            onHelpful={onHelpfulAnswer}
            onUnhelpful={onUnhelpfulAnswer}
          />
        ) : (
          <CommentaryBlock item={item} />
        );
      break;
    case 'human_input':
      content = <HumanInputBlock item={item} onAnswer={onAnswerHumanInput} />;
      break;
    case 'plan':
    case 'notice':
      content = <PlanOrNoticeBlock item={item} />;
      break;
    case 'agent_call':
      content = <AgentCallBlock item={item} />;
      break;
    default:
      content = <UnknownWorkItem item={item} />;
  }

  return (
    <div className={cn('min-w-0', className)} data-work-item-id={item.id}>
      {content}
    </div>
  );
}
